import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, ShoppingBag, RefreshCw, AlertTriangle } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { QtyControl, EmptyState, ProductImage } from '../components/ui/index.jsx'
import { useCartStore, useAuthStore } from '../store/index.js'
import { cartService, authService } from '../services/api.js'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

export default function Cart() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { items, removeItem, updateQty, total, clearCart } = useCartStore()

  const [serverCart, setServerCart] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [clearConfirm, setClearConfirm] = useState(false)

  const { data: customerProfile } = useQuery({
    queryKey: ['customer', 'me'],
    queryFn: authService.customerMe,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })
  const canOrder = !customerProfile || customerProfile.can_order !== false

  // Always use local cart items for display (they have name + price shaped by the store)
  const cartItems = items
  // Compute directly from items — getter not reliable after localStorage hydration
  const cartTotal = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (i.qty || i.quantity || 1), 0)
  // B2B distributor — no delivery fee
  const grandTotal = cartTotal

  const fetchServerCart = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const data = await cartService.get()
      setServerCart(data)
    } catch { /* fallback local */ }
  }, [isAuthenticated])

  useEffect(() => { fetchServerCart() }, [fetchServerCart])

  const handleRemove = async (item) => {
    const cartItemId = item.cart_item_id || item.id
    if (isAuthenticated && item.cart_item_id) {
      setSyncing(true)
      try {
        await cartService.removeItem(item.cart_item_id)
        await fetchServerCart()
        toast.success('Removed from cart', { duration: 1500 })
      } catch { toast.error('Failed to remove item') }
      finally { setSyncing(false) }
    } else {
      removeItem(cartItemId)
      toast.success('Removed from cart', { duration: 1500 })
    }
  }

  const handleQty = async (item, newQty) => {
    if (newQty <= 0) { handleRemove(item); return }
    if (isAuthenticated && item.cart_item_id) {
      setSyncing(true)
      try {
        await cartService.updateItem(item.cart_item_id, newQty)
        await fetchServerCart()
      } catch { toast.error('Failed to update quantity') }
      finally { setSyncing(false) }
    } else {
      updateQty(item.id, newQty)
    }
  }

  const handleClear = async () => {
    if (isAuthenticated) {
      setSyncing(true)
      try {
        await cartService.clear()
        setServerCart(null)
        clearCart()            // also wipe local Zustand store
        toast.success('Cart cleared')
      } catch { toast.error('Failed to clear cart') }
      finally { setSyncing(false) }
    } else {
      clearCart()
      toast.success('Cart cleared')
    }
    setClearConfirm(false)
  }

  if (cartItems.length === 0) return (
    <div className="page-container">
      <TopHeader title="My Cart" showBack backTo="/" showCart={false} />
      <EmptyState
        icon={ShoppingBag}
        title="Your cart is empty"
        message="Add some products to get started"
        action={() => navigate('/shop')}
        actionLabel="Browse Products"
      />
    </div>
  )

  return (
    <div className="min-h-screen bg-surface-dark">
      <TopHeader title="My Cart" showBack backTo="/" showCart={false} />

      <div className="pt-20 px-4 pb-40 space-y-3">
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <span className="font-display text-gray-400 text-sm">
              {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
            </span>
            {syncing && <RefreshCw size={12} className="text-primary animate-spin" />}
          </div>
          <button onClick={() => setClearConfirm(true)} disabled={syncing} className="text-red-400 text-sm font-display">
            Clear all
          </button>
        </div>

        {cartItems.map(item => {
          const id = item.cart_item_id || item.id
          const name = item.product?.product_name || item.product?.name || item.name || 'Product'
          const price = parseFloat(item.price || item.product?.inclprice2 || 0)
          const qty = item.quantity || item.qty || 1
          const category = item.product?.category_name || item.category || ''
          const stockCode = item.product?.stock_code || item.stock_code || ''

          return (
            <div key={id} className="bg-surface-card rounded-2xl border border-surface-border p-3 flex gap-3">
              <div className="w-20 h-20 bg-surface-elevated rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                <ProductImage
                  product={item.product || item}
                  className="w-full h-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-500 font-display">{category}</p>
                <p className="text-sm text-white font-display font-medium line-clamp-2">{name}</p>
                <p className="price text-sm mt-1">R{(price * qty).toFixed(2)}</p>
                <div className="flex items-center justify-between mt-2">
                  <QtyControl
                    qty={qty}
                    onIncrease={() => handleQty(item, qty + 1)}
                    onDecrease={() => handleQty(item, qty - 1)}
                    onSet={(n) => handleQty(item, n)}
                  />
                  <button
                    onClick={() => handleRemove(item)}
                    disabled={syncing}
                    className="w-7 h-7 rounded-lg bg-red-900/20 border border-red-900/30 flex items-center justify-center"
                  >
                    <Trash2 size={13} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {/* Order summary */}
        <div className="bg-surface-card rounded-2xl border border-surface-border p-4 space-y-3">
          <h3 className="font-heading text-white">Order Summary</h3>
          <div className="space-y-2">
            {[['Subtotal', `R${cartTotal.toFixed(2)}`]].map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <span className="text-gray-400 font-display text-sm">{label}</span>
                <span className={`font-display font-semibold text-sm ${value === 'FREE' ? 'text-green-400' : 'text-white'}`}>{value}</span>
              </div>
            ))}
            <div className="border-t border-surface-border pt-2 flex justify-between">
              <span className="font-display font-bold text-white">Total</span>
              <span className="font-heading text-gold text-lg">R{grandTotal.toFixed(2)}</span>
            </div>
          </div>

        </div>

        {!isAuthenticated && (
          <div className="bg-primary/10 border border-primary/30 rounded-2xl p-3">
            <p className="text-gray-300 text-sm font-display text-center">
              <button onClick={() => navigate('/login')} className="text-primary font-semibold">Sign in</button>
              {' '}to save your cart and track orders
            </p>
          </div>
        )}
      </div>

      {/* Clear cart confirmation modal */}
      {clearConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setClearConfirm(false)} />
          <div className="relative bg-surface-card border border-surface-border rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <div className="w-14 h-14 bg-red-900/30 border border-red-800/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-400" />
            </div>
            <h3 className="font-heading text-white text-lg text-center mb-1">Clear your cart?</h3>
            <p className="text-sm text-center text-gray-400 font-display mb-1">
              This will remove all{' '}
              <span className="font-semibold text-gray-200">{cartItems.length} item{cartItems.length !== 1 ? 's' : ''}</span>{' '}
              from your cart.
            </p>
            <p className="text-xs text-center text-gray-500 font-display mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={handleClear}
                disabled={syncing}
                className="flex-1 bg-red-600 active:bg-red-700 text-white rounded-xl py-3 text-sm font-display font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {syncing
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><Trash2 size={14} /> Yes, Clear It</>}
              </button>
              <button
                onClick={() => setClearConfirm(false)}
                className="flex-1 border border-surface-border text-gray-300 rounded-xl py-3 text-sm font-display font-semibold active:bg-surface-elevated"
              >
                Keep Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface-card border-t border-surface-border px-4 pt-3 space-y-2"
        style={{ paddingBottom: 'max(1.25rem, var(--safe-bottom))' }}>
        <div className="flex justify-between items-center">
          <span className="font-display text-gray-400 text-sm">Total</span>
          <span className="font-heading text-gold text-xl">R{grandTotal.toFixed(2)}</span>
        </div>
        {!canOrder && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            <span>Account on hold — outstanding balance. Contact us to place orders.</span>
          </div>
        )}
        {canOrder ? (
          <button onClick={() => navigate('/checkout')} className="btn-primary w-full text-base py-4">
            Proceed to Checkout →
          </button>
        ) : (
          <button disabled className="w-full py-4 rounded-xl bg-gray-700 text-gray-500 font-display font-semibold text-base cursor-not-allowed flex items-center justify-center gap-2">
            <AlertTriangle size={16}/> Ordering Suspended
          </button>
        )}
      </div>
    </div>
  )
}
